/**
 * Die Fehlermeldung des Backends aus einem fehlgeschlagenen SDK-/Axios-Aufruf
 * lesen — statt Axios' „Request failed with status code 400“.
 *
 * NestJS antwortet mit `{ statusCode, message }`; `message` ist ein String
 * (`HttpException`) oder ein Array (class-validator). Strukturell gelesen
 * statt über `axios.isAxiosError`, damit `npm test` (node --test) ohne axios
 * auskommt.
 *
 * Es gibt im Code noch weitere, lokal definierte Varianten desselben Musters
 * (z. B. `pages/email/edit_email_draft.tsx`, `pages/order_inbox/**`); neue
 * Aufrufer sollten diese hier verwenden.
 */

/** NestJS' Standardtext für unbehandelte 500er — für Nutzer wertlos. */
const GENERIC_SERVER_MESSAGES = new Set(["Internal server error"]);

function messageFromBody(data: unknown): string | null {
  if (typeof data !== "object" || data === null || !("message" in data)) {
    return null;
  }
  const raw = (data as { message?: unknown }).message;
  const text = Array.isArray(raw)
    ? raw.filter((m): m is string => typeof m === "string").join(" ")
    : typeof raw === "string"
      ? raw
      : "";
  const trimmed = text.trim();
  if (!trimmed || GENERIC_SERVER_MESSAGES.has(trimmed)) return null;
  return trimmed;
}

function responseBody(error: unknown): unknown {
  if (typeof error !== "object" || error === null || !("response" in error)) {
    return undefined;
  }
  return (error as { response?: { data?: unknown } }).response?.data;
}

/** Die vom Backend mitgeschickte Meldung, sonst `null`. */
export function backendErrorMessage(error: unknown): string | null {
  return messageFromBody(responseBody(error));
}

function transportMessage(error: unknown): string | null {
  return error instanceof Error && error.message ? error.message : null;
}

/**
 * „<prefix>: <Backend-Meldung>“; ohne Backend-Meldung die technische
 * Fehlermeldung (für den Support), ohne beides nur der Prefix.
 */
export function describeApiError(prefix: string, error: unknown): string {
  const detail = backendErrorMessage(error) ?? transportMessage(error);
  return detail ? `${prefix}: ${detail}` : prefix;
}

/**
 * Wie {@link describeApiError}, liest aber auch Fehler-Bodies von Downloads
 * (`responseType: 'blob'`): dort liefert Axios auch das JSON des 4xx als
 * Blob, `response.data.message` gibt es dann nicht direkt.
 */
export async function describeApiErrorAsync(prefix: string, error: unknown): Promise<string> {
  const body = responseBody(error);
  if (body && typeof (body as Blob).text === "function") {
    try {
      const fromBlob = messageFromBody(JSON.parse(await (body as Blob).text()));
      if (fromBlob) return `${prefix}: ${fromBlob}`;
    } catch (_) {
      // Not JSON (e.g. an HTML error page) — fall through.
    }
  }
  return describeApiError(prefix, error);
}
